import { APITypesV1 } from "@cur8/api-client";
import { useMutation } from "@tanstack/react-query";
import { APIClient } from "lib/api/client";
import { useAPIClient } from "render/context/APIContext";
import { useFeaturePreference } from "render/hooks/useFeaturePreference";

export type TubeDrawDecision = { bloodSampleId: string } & (
  | { decision: "drawn" }
  | { decision: "skipped"; reason: APITypesV1.SkipReason }
);

type Variables = {
  patientId: string;
  decisions: TubeDrawDecision[];
};

export type TubeDrawFailure = {
  bloodSampleId: string;
  error: unknown;
};

/**
 * At least one tube didn't land. Carries which ones so the operator can be told
 * what to try again, rather than which tubes did make it.
 */
export class TubeDrawDecisionError extends Error {
  constructor(readonly failures: TubeDrawFailure[]) {
    super(`${failures.length} tube(s) were not recorded`);
    this.name = "TubeDrawDecisionError";
  }
}

function recordDecision(
  api: APIClient,
  patientId: string,
  decision: TubeDrawDecision
) {
  if (decision.decision === "drawn") {
    return api.bloodSample.confirmSampleDraw({
      patientId,
      bloodSampleId: decision.bloodSampleId,
    }).result;
  }
  return api.bloodSample.skipSampleDraw(
    { patientId, bloodSampleId: decision.bloodSampleId },
    { reason: decision.reason }
  ).result;
}

// The pre-AB#91601 path: every tube in one request, on the route swagger now
// marks deprecated. Kept for singleVialConfirmEnabled being off, so a clinic
// that hasn't taken the new flow yet confirms tubes exactly as it does today.
function recordDecisionsInBulk(
  api: APIClient,
  patientId: string,
  decisions: TubeDrawDecision[]
) {
  return api.bloodSample.confirmDraw(
    { patientId },
    {
      drawnBloodSampleIds: decisions
        .filter((decision) => decision.decision === "drawn")
        .map((decision) => decision.bloodSampleId),
      skippedBloodSamples: decisions.flatMap((decision) =>
        decision.decision === "skipped"
          ? [{ bloodSampleId: decision.bloodSampleId, reason: decision.reason }]
          : []
      ),
    }
  ).result;
}

// No onError here by design — DrawConfirmationView surfaces the error inline
// and keeps the local decisions intact so Done can just be retried. Both
// per-vial endpoints are idempotent, so a retry re-sends the tubes that already
// landed without recording them twice.
export function useConfirmDrawMutation() {
  const api = useAPIClient();
  const { data: featurePreferences } = useFeaturePreference();
  const perVial = featurePreferences.singleVialConfirmEnabled;

  return useMutation({
    async mutationFn({ patientId, decisions }: Variables) {
      if (!perVial) {
        await recordDecisionsInBulk(api, patientId, decisions);
        return;
      }

      // One call per tube, and a tube that fails doesn't take the ones after it
      // down with it: getting the plasma tube to the lab is the whole point of
      // this flow, so it can't hang on the tube the operator is stuck on.
      const failures: TubeDrawFailure[] = [];
      for (const decision of decisions) {
        try {
          await recordDecision(api, patientId, decision);
        } catch (error) {
          failures.push({ bloodSampleId: decision.bloodSampleId, error });
        }
      }
      if (failures.length > 0) {
        throw new TubeDrawDecisionError(failures);
      }
    },
  });
}
